import { useEffect, useMemo, useRef, useState } from "react";
import { useClientsState } from "atoms/clientsAtom";
import { Client } from "types";
import { useSetAtom } from "jotai/index";

import { SearchInput } from "components/SearchInput/SearchInput";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";
import { Row } from "components/Row/Row";
import { MediumText } from "components/MediumText/MediumText";
import { Divider } from "components/Divider/Divider";
import { Paragraph } from "components/Paragraph/Paragraph";
import { Checkbox2 } from "components/Checkbox2/Checkbox2";

import { setClientModalAtom } from "../../../atoms/modalAtom/modalAtom";

import { useInfiniteClients } from "../api";
import styles from "../styles.module.scss";

const SCROLL_END_THRESHOLD = 400;

function getScrollParent(el: Element | null): Element | null {
  if (!el) return null;
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/.test(overflowY)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

export const ClientsBody = (): JSX.Element => {
  const setClientModal = useSetAtom(setClientModalAtom);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  const { clientsState, setClientsState } = useClientsState();
  const [search, setSearch] = useState("");

  const groupId = clientsState.activeGroupItem?.clientgroupid ?? null;
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteClients({ clientgroupid: groupId, search });

  const rawItems = isLoading ? [] : (data?.pages?.flat() ?? []);
  const clients: Client[] = Array.isArray(rawItems)
    ? rawItems.filter((x): x is Client => x != null && typeof x === "object")
    : [];

  const onItemClick = (item: Client) => {
    if (clientsState.activeItem?.clientid === item.clientid) {
      setClientModal(true);
    } else {
      setClientsState((prev) => ({
        ...prev,
        activeItem: item,
      }));
    }
  };

  const handleCheckUncheck = (item: Client) => {
    const isActive = clientsState.activeItems.some(
      (temp) => temp.clientid === item.clientid
    );

    if (isActive) {
      setClientsState((prev) => ({
        ...prev,
        activeItems: prev.activeItems.filter(
          (temp) => temp.clientid !== item.clientid
        ),
      }));
    } else {
      setClientsState((prev) => ({
        ...prev,
        activeItems: [...prev.activeItems, item],
      }));
    }
  };

  const handleCheckUncheckAll = () => {
    setClientsState((prev) => ({
      ...prev,
      activeItems:
        clientsState.activeItems.length === clients.length ? [] : [...clients],
    }));
  };

  const allChecked = useMemo(() => {
    return Boolean(
      clients.length && clientsState.activeItems.length === clients.length
    );
  }, [clients, clientsState.activeItems]);

  useEffect(() => {
    if (clientsState.activeItems.length) {
      setClientsState((prev) => ({
        ...prev,
        activeItems: clients.filter((c) =>
          prev.activeItems.some((a) => a.clientid === c.clientid)
        ),
      }));
    }
  }, [clients]);

  useEffect(() => {
    isLoadingRef.current = Boolean(isFetchingNextPage);
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const root = getScrollParent(sentinel) ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        fetchNextPage();
      },
      {
        root,
        rootMargin: `0px 0px ${SCROLL_END_THRESHOLD}px 0px`,
        threshold: 0,
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <div className={styles.container}>
      <VerticalSpace size={8} />
      <div className="flex items-center justify-start w-full p-2">
        <div className="flex-1">
          <SearchInput callback={setSearch} />
        </div>
      </div>
      <Row>
        <Checkbox2 onChange={handleCheckUncheckAll} checked={allChecked} />
        <HorizontalSpace size={18} />
        <MediumText style={{ width: "400px" }}>ФИО/Наименование</MediumText>
        <MediumText style={{ width: "150px" }}>Телефон</MediumText>
        <MediumText style={{ width: "150px" }}>Процент скидки</MediumText>
      </Row>
      <Divider />
      <div className={styles.clientsList}>
      {clients.map((item: Client) => {
        const isActive = clientsState.activeItems.some(
          (temp) => temp.clientid === item.clientid
        );

        const isSelected = clientsState.activeItem?.clientid === item.clientid;

        return (
          <Row
            key={item.clientid}
            isActive={isSelected}
            onClick={() => onItemClick(item)}
          >
            <div
              onClick={(ev) => {
                ev.stopPropagation();
                handleCheckUncheck(item);
              }}
              className={styles.checkboxWrapper}
            >
              <Checkbox2
                checked={isActive}
                onClick={(ev) => {
                  ev.stopPropagation();
                }}
              />
            </div>
            <HorizontalSpace size={18} />
            <Paragraph width={400}>
              {item.firma === 0
                ? `${item.firstname} ${item.secondname}`
                : item.firstname}
            </Paragraph>
            <Paragraph width={150}>{item.phone}</Paragraph>
            <Paragraph width={150}>{item.percent}</Paragraph>
          </Row>
        );
      })}
      {hasNextPage && <div ref={sentinelRef} className={styles.loadingSentinel} />}
      {isFetchingNextPage && (
        <div className={styles.loadingMore}>Загрузка...</div>
      )}
      </div>
    </div>
  );
};
